// analytics-implementation.js
// Add this file to your project and import it in common.js

/**
 * TEMPROM Analytics Implementation
 * 
 * This module implements:
 * 1. Heat mapping via Microsoft Clarity
 * 2. Time tracking on producer vs DJ value propositions
 * 3. Click-through tracking for "Get Early Access" buttons
 */

// =======================================
// HEAT MAPPING IMPLEMENTATION
// =======================================

function setupHeatMapping() {
  // Add Microsoft Clarity (free heat mapping tool)
  // Add this to the <head> of your HTML or inject dynamically
  const clarityScript = document.createElement('script');
  clarityScript.type = 'text/javascript';
  clarityScript.innerHTML = `
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "qrnptcn0jt");
  `;
  document.head.appendChild(clarityScript);
}

// =======================================
// TIME TRACKING IMPLEMENTATION
// =======================================

// Store timing data
const timingData = {
  producerSection: {
    totalTime: 0,
    visits: 0,
    entryTime: 0
  },
  djSection: {
    totalTime: 0,
    visits: 0,
    entryTime: 0
  }
};

// Track when user scrolls to a section
function setupSectionTimeTracking() {
  // Define sections to track
  const sections = {
    producerSection: document.querySelector('#producer-tab'), // Producer tab content
    djSection: document.querySelector('#dj-tab')  // DJ tab content
  };
  
  // Set up IntersectionObserver to detect when sections are in view
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const sectionId = entry.target.id.replace('-tab', 'Section');
      
      if (entry.isIntersecting) {
        // User entered section
        timingData[sectionId].entryTime = Date.now();
        timingData[sectionId].visits++;
      } else if (timingData[sectionId].entryTime > 0) {
        // User left section
        const timeSpent = Date.now() - timingData[sectionId].entryTime;
        timingData[sectionId].totalTime += timeSpent;
        timingData[sectionId].entryTime = 0;
        
        // Send data to Google Analytics
        gtag('event', 'section_view', {
          'event_category': 'engagement',
          'event_label': sectionId,
          'value': Math.round(timeSpent / 1000) // Convert to seconds
        });
      }
    });
  }, { threshold: 0.5 }); // 50% of section must be visible
  
  // Start observing sections
  Object.values(sections).forEach(section => {
    if (section) {
      observer.observe(section);
    }
  });
  
  // Send final data on page unload
  window.addEventListener('beforeunload', () => {
    // Check if we need to add final time for current section
    Object.keys(timingData).forEach(sectionId => {
      if (timingData[sectionId].entryTime > 0) {
        const timeSpent = Date.now() - timingData[sectionId].entryTime;
        timingData[sectionId].totalTime += timeSpent;
        
        // Send final data to Google Analytics
        gtag('event', 'total_section_time', {
          'event_category': 'engagement',
          'event_label': sectionId,
          'value': Math.round(timingData[sectionId].totalTime / 1000) // Convert to seconds
        });
      }
    });
  });
}

// =======================================
// CLICK-THROUGH TRACKING
// =======================================

function setupClickTracking() {
  // Track all CTA buttons
  const trackButtons = () => {
    // Get all CTA buttons
    const ctaButtons = document.querySelectorAll('#cta-get-started, #how-it-works-cta, #cta-signup');
    
    // Add click event listeners
    ctaButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        // Determine which section the user is viewing when clicking
        const producerTab = document.querySelector('#producer-tab');
        const djTab = document.querySelector('#dj-tab');
        
        let userContext = 'general';
        
        if (producerTab && producerTab.classList.contains('active')) {
          userContext = 'producer';
        } else if (djTab && djTab.classList.contains('active')) {
          userContext = 'dj';
        }
        
        // Capture button ID and context
        gtag('event', 'cta_click', {
          'event_category': 'conversion',
          'event_label': button.id,
          'user_context': userContext,
          'button_text': button.textContent.trim()
        });
      });
    });
    
    // Also track producer and DJ specific buttons
    const roleButtons = document.querySelectorAll('.producer-btn, .dj-btn');
    roleButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const role = button.classList.contains('producer-btn') ? 'producer' : 'dj';
        
        gtag('event', 'role_selection', {
          'event_category': 'conversion',
          'event_label': role,
          'button_text': button.textContent.trim()
        });
      });
    });
  };
  
  // Track form submissions to measure final conversion
  const trackFormSubmission = () => {
    const signupForm = document.getElementById('signupForm');
    if (signupForm) {
      const originalSubmitHandler = signupForm.onsubmit;
      
      signupForm.addEventListener('submit', function(e) {
        // Get selected role
        // const userType = document.getElementById('userType').value;
        
        // Track conversion
        gtag('event', 'signup_completion', {
          'event_category': 'conversion',
          // 'event_label': userType,
          'event_label': 'producer',
          'conversion': true
        });
        
        // Continue with original handler
        if (originalSubmitHandler) {
          return originalSubmitHandler(e);
        }
      });
    }
  };
  
  // Run tracking setup
  trackButtons();
  trackFormSubmission();
}

// No public dashboard - removed insecure analytics dashboard implementation
// Data will be properly secured and accessed through the standalone dashboard application

// =======================================
// INITIALIZATION
// =======================================

export function initAnalytics() {
  // Initialize all tracking components
  setupHeatMapping();
  setupSectionTimeTracking();
  setupClickTracking();
  
  console.log('TEMPROM Analytics Initialized');
}